/* FTE arithmetic and formatting — designs/per-month-role-needs.md PN17/PN18.

   TWO rules live here, and both exist because of real reported bugs.

   1. WORK IN INTEGER TENTHS. `RoleNeed.count` moves in steps of 0.1
      (allocations.md A10) and allocation percents are whole numbers, so
      every FTE quantity in this app is an exact multiple of a tenth. Summing
      them as binary floats is not — `0.1 + 0.2 !== 0.3` — and the moment a
      schedule is summed that noise reaches a screen. An integer tenth is
      exact, so it cannot.

   2. FIGURES SHOWN TOGETHER MUST RECONCILE. A remainder printed beside its
      operands is computed from the ROUNDED operands, never rounded
      independently from the exact ones. Trevor, Aug 28 2026: *"I have seen
      some weird rounding errors in the past where we say 1.3 FTEs of 2.0
      FTES. Needed 0.8 FTES"* — a 2.0 need with 60 % + 65 % allocated, where
      1.25 and 0.75 each rounded up on their own and the sentence stopped
      being arithmetic. Subtract in tenths and print the difference.

   Percent stays the unit wherever the thing measured really is a share of
   one person's time — the allocation record, the allocation form, the 100 %
   caps and the Teams per-person bars. This module is for the other side of
   that boundary: what a PROJECT needs and has. */

/* Halves go AWAY FROM ZERO, deliberately. `Math.round` is half-up, so it
   sends 0.5 to 1 but -0.5 to -0 — an over-filled slot would then round the
   opposite way to an under-filled one of the same magnitude. */
const roundHalfAway = (v: number): number => Math.sign(v) * Math.round(Math.abs(v));

/* An FTE quantity as integer tenths — the display grain. */
export const tenths = (fte: number): number => roundHalfAway(fte * 10);

/* Whole percent of one person's time as integer tenths of an FTE: a person
   at 60 % is 0.6 FTE, and 125 % across two people is 1.3. */
export const tenthsOfPercent = (percent: number): number => roundHalfAway(percent / 10);

/* Tenths as the app has always written FTE (A10): "2", "2.5", "0.6" — a
   whole number is trimmed, so `3` never prints as `3.0`. */
export function fmtTenths(value: number): string {
  const sign = value < 0 ? "-" : "";
  const size = Math.abs(value);
  const whole = Math.trunc(size / 10);
  const tenth = size % 10;
  return `${sign}${whole}${tenth ? `.${tenth}` : ""}`;
}

/* The same, from an FTE number — for values that were never summed. */
export const fmtFte = (fte: number): string => fmtTenths(tenths(fte));
